import { Fragment } from 'react';
import { Pane, Text, Badge, Tooltip } from 'evergreen-ui';
import { useRole } from '../../../contexts/RoleContext';
import { isAbsence, absenceColor, cleanNotes } from '../../../utils/turniAbsences';

const BASE_STYLE = { whiteSpace: 'normal', height: 'auto', lineHeight: '1.5' };
const DESKTOP_STYLE = { ...BASE_STYLE, overflow: 'hidden', textOverflow: 'ellipsis' };
const MOBILE_PADDING = { paddingX: 8, paddingY: 4 };

const hhmm = (t) => (t ? t.substring(0, 5) : '');

// " 09:00-11:00" solo per ferie/permesso parziali (00:00 = giornata intera)
const partialTimeRange = (type, start, end) =>
  (['permesso', 'ferie'].includes(type) && start && hhmm(start) !== '00:00' && end ? ` ${hhmm(start)}-${hhmm(end)}` : '');

const getShiftPeriodBadge = (shift_period, fallbackColor) => {
  if (shift_period === 'giorno') return { color: 'blue', extraStyle: {} };
  if (shift_period === 'notte') return { color: 'neutral', extraStyle: { backgroundColor: '#1a1a1a', color: '#fff' } };
  if (shift_period === 'coda_della_notte') return { color: 'orange', extraStyle: {} };
  return { color: fallbackColor, extraStyle: {} };
};

function LeaveBadge({ leave, ctx }) {
  const { isDirector, getInitials, onLeaveDragStart, badgeProps, style } = ctx;
  const initial = (leave.leave_type || 'FER').substring(0, 1).toUpperCase();
  const text = isDirector
    ? `${initial}${partialTimeRange(leave.leave_type, leave.start_time, leave.end_time)} - ${getInitials(leave.operator_name)}`
    : `ASS - ${getInitials(leave.operator_name)}`;
  return (
    <Tooltip content={isDirector ? (leave.motivazione || 'Assenza') : 'Assenza'}>
      <Badge color={isDirector ? absenceColor(leave.leave_type) : 'neutral'} isSolid {...badgeProps} draggable={isDirector}
        onDragStart={(e) => { e.stopPropagation(); if (isDirector) onLeaveDragStart(e, leave.id); }}
        style={{ ...style, cursor: isDirector ? 'grab' : 'default' }}>
        {text}
      </Badge>
    </Tooltip>
  );
}

const shiftBadgeText = (s, absence, { isDirector, getInitials, getBadgeProps }) => {
  const who = getInitials(s.operator_name);
  if (absence && !isDirector) return `ASS - ${who}`;
  if (absence) return `${getBadgeProps(s.shift_type).text}${partialTimeRange(s.shift_type, s.start_time, s.end_time)} - ${who}`;
  return `${hhmm(s.start_time) || '?'} / ${hhmm(s.end_time) || '?'} - ${who}`;
};

function ShiftBadge({ shift: s, ctx }) {
  const { isDirector, getBadgeProps, onShiftDragStart, onShiftClick, badgeProps, style } = ctx;
  const absence = isAbsence(s.shift_type);
  const bp = getBadgeProps(s.shift_type);
  const { color, extraStyle } = absence ? { color: isDirector ? bp.color : 'neutral', extraStyle: {} } : getShiftPeriodBadge(s.shift_period, bp.color);
  const noteText = cleanNotes(s.notes) || 'Nessuna nota';
  return (
    <Fragment>
      <Tooltip content={isDirector ? noteText : (absence ? 'Assenza' : noteText)}>
        <Badge color={color} isSolid {...badgeProps} style={{ ...style, cursor: isDirector ? 'grab' : 'default', ...extraStyle }}
          draggable={isDirector} onDragStart={(e) => { e.stopPropagation(); if (isDirector) onShiftDragStart(e, s.id); }}
          onClick={(e) => { e.stopPropagation(); if (isDirector && !absence) onShiftClick(s); }}>
          {shiftBadgeText(s, absence, ctx)}
        </Badge>
      </Tooltip>
      {!absence && s.notte_attiva_intervalli && (
        <Badge color="red" isSolid {...badgeProps} style={BASE_STYLE}>⚠ Emergenza</Badge>
      )}
    </Fragment>
  );
}

function RepBadge({ rep, ctx }) {
  return (
    <Tooltip content={rep.operator_name}>
      <Badge color="purple" isSolid {...ctx.badgeProps} style={ctx.style}>
        {rep.period === 'notte' ? '☾' : '☀'} {hhmm(rep.start_time)} / {hhmm(rep.end_time)} - {ctx.getInitials(rep.operator_name)}
      </Badge>
    </Tooltip>
  );
}

// Badge di un giorno della vista mensile, condivisi tra layout mobile e desktop
export default function MonthDayBadges({ day, turniData, managerFerieList, reperibilitaData, isMobile, ...handlers }) {
  const { effectiveIsDirector: isDirector } = useRole();
  const ctx = { ...handlers, isDirector, badgeProps: isMobile ? MOBILE_PADDING : {}, style: isMobile ? BASE_STYLE : DESKTOP_STYLE };
  const dayShifts = turniData.filter(t => t.date === day.fullDate);
  const reps = (reperibilitaData || []).filter(r => r.date === day.fullDate);
  // Una richiesta approvata non si ripete se esiste già il turno-assenza corrispondente
  const leaves = managerFerieList.filter(l => l.start_date <= day.fullDate && l.end_date >= day.fullDate && l.status === 'approvata'
    && !dayShifts.some(s => s.operator_name === l.operator_name && isAbsence(s.shift_type)));
  const sortedShifts = [...dayShifts].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  const isEmpty = leaves.length === 0 && dayShifts.length === 0 && reps.length === 0;

  return (
    <Pane {...(isMobile ? { flexWrap: 'wrap', gap: 8 } : { marginTop: 8, flexDirection: 'column', gap: 4 })} display="flex">
      {leaves.map(l => <LeaveBadge key={`l_${l.id}`} leave={l} ctx={ctx} />)}
      {sortedShifts.map(s => <ShiftBadge key={`s_${s.id}`} shift={s} ctx={ctx} />)}
      {reps.map((r, idx) => <RepBadge key={`r_${r.id ?? idx}`} rep={r} ctx={ctx} />)}
      {isMobile && isEmpty && <Text size={300} color="muted">Nessun turno assegnato</Text>}
    </Pane>
  );
}
